import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

export const PerformanceChart = () => {
  const { data: performanceData, isLoading, error } = useQuery({
    queryKey: ['lenderPerformance'],
    queryFn: async () => {
      // First get the lender ID for the current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: lender, error: lenderError } = await supabase
        .from('lenders')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (lenderError) throw lenderError;
      if (!lender) throw new Error('No lender found');

      const { data: loans, error } = await supabase
        .from('loan_applications')
        .select('amount, created_at')
        .eq('lender_id', lender.id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Group and aggregate data by month
      const monthlyData = loans?.reduce((acc, loan) => {
        const month = new Date(loan.created_at).toLocaleString('default', { month: 'short' });
        acc[month] = (acc[month] || 0) + (loan.amount || 0);
        return acc;
      }, {} as Record<string, number>);

      return Object.entries(monthlyData || {}).map(([month, amount]) => ({
        month,
        amount,
      }));
    },
  });

  if (error) {
    console.error('Error loading performance data:', error);
    return (
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Portfolio Performance</h2>
        <div className="flex justify-center items-center h-[300px]">
          <p className="text-red-500">Error loading performance data</p>
        </div>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Portfolio Performance</h2>
        <div className="flex justify-center items-center h-[300px]">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      </Card>
    );
  }

  if (!performanceData || performanceData.length === 0) {
    return (
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Portfolio Performance</h2>
        <div className="flex justify-center items-center h-[300px]">
          <p className="text-muted-foreground">No performance data available</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h2 className="text-lg font-semibold mb-4">Portfolio Performance</h2>
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={performanceData}>
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Area
              type="monotone"
              dataKey="amount"
              stroke="#2563eb"
              fill="#2563eb"
              fillOpacity={0.2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};